import {
  components,
  OptionProps,
  ValueContainerProps,
  GroupBase,
} from "react-select";
import type { ReactNode } from "react";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import { CheckMiniIcon, RecommendedTelemetryIcon } from "./svgs/TestCaseSvgs";

export interface SelectOption {
  label: string;
  value: unknown;
  isRecommended?: boolean;
  isSecondary?: boolean;
  isUnassociated?: boolean;
  isSelectedPinned?: boolean;
  sectionLabel?: string;
  showSectionDivider?: boolean;
  highlightTerms?: string[];
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getHighlightedLabel = (label: string, terms?: string[]): ReactNode => {
  const validTerms = Array.from(new Set((terms ?? []).map((term) => term.trim()).filter(Boolean)));
  if (validTerms.length === 0) return label;

  const pattern = new RegExp(`(${validTerms.map(escapeRegExp).join("|")})`, "ig");
  return label.split(pattern).map((part, idx) => {
    const isMatch = validTerms.some((term) => part.toLowerCase() === term.toLowerCase());
    return isMatch ? <mark key={`${part}-${idx}`} className="tcd-tm-option__match">{part}</mark> : part;
  });
};

export const TelemetryOption = (props: OptionProps<SelectOption, true, GroupBase<SelectOption>>): JSX.Element => {
  const { data, isSelected, innerRef, innerProps } = props;
  const sectionClassName = [
    "tcd-tm-option__section",
    data.isRecommended ? "recommended" : "",
    data.isSecondary ? "secondary" : "",
    data.isUnassociated ? "unassociated" : "",
    data.isSelectedPinned ? "selected" : "",
  ].filter(Boolean).join(" ");

  return (
    <div ref={innerRef} {...innerProps} className="tcd-tm-option">
      {data.showSectionDivider && <div className="tcd-tm-option__divider" />}
      {data.sectionLabel && (
        <div className={sectionClassName}>
          <span>{data.sectionLabel}</span>
        </div>
      )}
      <div className={`tcd-tm-option__main${data.isRecommended ? " recommended" : ""}${data.isSecondary ? " secondary" : ""}${data.isUnassociated ? " unassociated" : ""}${data.isSelectedPinned ? " selected" : ""}`}>
        <span className={`tcd-tm-option__cb${isSelected ? " checked" : ""}`}>
          {isSelected && <CheckMiniIcon />}
        </span>
        <CustomToolTip title={data.label} placement="right" maxWidth="260px" className="tcd-tm-option__tooltip">
          <span className="tcd-tm-option__label">{getHighlightedLabel(data.label, data.highlightTerms)}</span>
        </CustomToolTip>
        {data.isRecommended && (
          <span className="tcd-tm-option__recommended-icon">
            <RecommendedTelemetryIcon />
          </span>
        )}
      </div>
    </div>
  );
};

export const TelemetryValueContainer = (props: ValueContainerProps<SelectOption, true, GroupBase<SelectOption>>): JSX.Element => {
  const { children, getValue } = props;
  const selected = getValue();
  const inputValue = props.selectProps.inputValue;
  const isTyping = !!inputValue;
  const shouldPlaceInputFirst = isTyping || !!props.selectProps.menuIsOpen;
  const inputChild = Array.isArray(children) ? children[children.length - 1] : children;
  const summaryNode = (
    <CustomToolTip
      title={selected.length === 1 ? selected[0].label : selected.map((option) => option.label).join(", ")}
      placement="top"
      maxWidth="360px"
      disabled={isTyping}
      className={`tcd-tm-value-tooltip${isTyping ? " typing" : ""}`}
    >
      <span className={`tcd-tm-value-label${selected.length > 1 ? " multi" : ""}`}>
        {selected.length === 1 ? selected[0].label : `${selected.length} selected`}
      </span>
    </CustomToolTip>
  );
  const inputNode = (
    <span className={`tcd-tm-value-input-shadow${isTyping ? " typing" : ""}${shouldPlaceInputFirst ? " leading" : ""}`}>
      {inputChild}
    </span>
  );

  return (
    <components.ValueContainer {...props}>
      {selected.length === 0 ? (
        children
      ) : (
        <>
          {shouldPlaceInputFirst ? inputNode : summaryNode}
          {shouldPlaceInputFirst ? summaryNode : inputNode}
        </>
      )}
    </components.ValueContainer>
  );
};
